import { AgentWorkspaceConstants } from "lookup/AgentWorkspaceConstants";
import { isStringEmptyOrNull } from "services/UtilityService";

/** PM-3092 Modal A — neither phone nor fax verified when both are required */
export const CONTACT_VERIFICATION_BOTH_MISSING = "BOTH_MISSING";
/** PM-3092 Modal B — phone-only gate, or remaining verification after Modal A rules */
export const CONTACT_VERIFICATION_PHONE_MISSING = "PHONE_MISSING";

/**
 * Human-readable label for call-group entity type (used in verification modals).
 * @param {string} entityType PRESCRIBER | PHARMACY | MEMBER (or similar from API)
 */
export function getEntityLabel(entityType) {
  if (!entityType) return "contact";
  switch (String(entityType).toUpperCase()) {
    case "PRESCRIBER":
      return "prescriber";
    case "PHARMACY":
      return "pharmacy";
    case "MEMBER":
      return "member";
    default:
      return String(entityType).toLowerCase();
  }
}

/**
 * PM-3092: whether submit should open the contact verification modal instead of closing immediately.
 * List A: close reason in closeReasonsForPhoneAndFaxVerification OR disposition in
 * switchDispositionsForPhoneAndFaxVerification (disposition alone is enough).
 * List A (close reasons or PVD/MVD dispositions): BOTH_MISSING unless both phone and fax are ticked.
 * List B: close reason in closeReasonsForPhoneOnlyVerification OR disposition in
 * switchDispositionsForPhoneOnlyVerification (all except PVD, MVD, No Fax Machine;
 * disposition alone is enough). Requires Verified Phone only.
 *
 * @returns {typeof CONTACT_VERIFICATION_BOTH_MISSING | typeof CONTACT_VERIFICATION_PHONE_MISSING | null}
 */
export function requiresContactVerification(
  closeReason,
  verifiedPhone,
  verifiedFax,
  entityType,
  switchDisposition
) {
  const disposition = switchDisposition || "";
  const hasCloseReason = !isStringEmptyOrNull(closeReason);

  const dualReasonFromCloseReason =
    hasCloseReason &&
    AgentWorkspaceConstants.closeReasonsForPhoneAndFaxVerification.includes(
      closeReason
    );
  const dualReasonFromDisposition =
    AgentWorkspaceConstants.switchDispositionsForPhoneAndFaxVerification.includes(
      disposition
    );

  const phoneOnlyReason =
    (hasCloseReason &&
      AgentWorkspaceConstants.closeReasonsForPhoneOnlyVerification.includes(
        closeReason
      )) ||
    AgentWorkspaceConstants.switchDispositionsForPhoneOnlyVerification.includes(
      disposition
    );

  const dualReason = dualReasonFromCloseReason || dualReasonFromDisposition;

  if (entityType === "MEMBER") {
    if (dualReason && !verifiedPhone) {
      return CONTACT_VERIFICATION_BOTH_MISSING;
    }
    if (phoneOnlyReason && !verifiedPhone) {
      return CONTACT_VERIFICATION_PHONE_MISSING;
    }
    return null;
  }

  if (entityType !== "PRESCRIBER" && entityType !== "PHARMACY") {
    return null;
  }

  if (dualReason && (!verifiedPhone || !verifiedFax)) {
    return CONTACT_VERIFICATION_BOTH_MISSING;
  }

  if (phoneOnlyReason && !verifiedPhone) {
    return CONTACT_VERIFICATION_PHONE_MISSING;
  }

  return null;
}

/**
 * Copy for {@link ConfirmationModal} when {@link requiresContactVerification} returned non-null.
 */
export function getContactVerificationModalCopy(scenario, entityLabel) {
  if (!scenario) return null;
  const label = entityLabel || "contact";

  if (scenario === CONTACT_VERIFICATION_BOTH_MISSING) {
    return {
      title: "Phone and fax verification",
      leadText: `Please confirm whether you verified the ${label}’s phone and fax numbers during this call.`,
      yesHint: "Return to the form to tick Verified Phone / Verified Fax before saving.",
      noHint: "Close the activity now (you attest phone/fax were NOT verified).",
    };
  }

  if (scenario === CONTACT_VERIFICATION_PHONE_MISSING) {
    return {
      title: "Contact verification",
      leadText: `Please confirm whether you verified the ${label}’s phone during this call.`,
      yesHint: "Return to the form to tick Verified Phone before saving.",
      noHint: "Close the activity now (you attest phone was NOT verified).",
    };
  }

  return null;
}
